import React from "react";
import Card from "../../../../components/ui/Card";
import Button from "../../../../components/ui/Button";
import AdminDataTable from "../../../admin/components/AdminDataTable";
import { PlusCircle, Edit } from "lucide-react";
import { mockCreator } from "../../../../data/mockData";
import RecentActivityList from "../../../../components/ui/RecentActivityList";

const subscriberColumns = [
  {
    header: "Name",
    key: "name",
    render: (row) => <p className="font-medium text-slate-800">{row.name}</p>,
  },
  {
    header: "Email",
    key: "email",
    render: (row) => row.email,
  },
  {
    header: "Tier",
    key: "tier",
    render: (row) => (
      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">
        {row.tier}
      </span>
    ),
  },
  {
    header: "Join Date",
    key: "joinDate",
    render: (row) => row.joinDate,
  },
];

const SubscriptionsTab = ({
  subscriptions: initialSubscriptions = [],
  onCreate,
  onEdit,
}) => {
  const subscriptionTransactions = mockCreator.mockTransactions
    .filter((txn) => txn.type === "Subscription")
    .slice(0, 5);

  const totalSubscribers = mockCreator.subscribersList.length;
  const mrr = mockCreator.subscribersList.reduce((acc, subscriber) => {
    const subProduct = initialSubscriptions.find(
      (s) => s.name === subscriber.tier
    );
    return acc + (subProduct ? subProduct.priceETB : 0);
  }, 0);

  return (
    <div className="space-y-6">
      {/* --- Analytics Cards --- */}
      <div className="grid grid-cols-1 @md:grid-cols-3 gap-4">
        <Card>
          <p className="text-sm text-slate-500">Total Subscribers</p>
          <p className="text-2xl font-bold text-slate-800">
            {totalSubscribers.toLocaleString()}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-slate-500">Monthly Recurring Revenue</p>
          <p className="text-2xl font-bold text-slate-800">
            {mrr.toLocaleString()} ETB
          </p>
        </Card>
        <Card>
          <p className="text-sm text-slate-500">Avg. Revenue / Subscriber</p>
          <p className="text-2xl font-bold text-slate-800">
            {(totalSubscribers > 0 ? mrr / totalSubscribers : 0).toFixed(2)} ETB
          </p>
        </Card>
      </div>

      {/* --- Subscription Products Section --- */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Subscriptions</h2>
            <p className="text-slate-500">Manage your subscription tiers and external platforms.</p>
          </div>
          <Button onClick={onCreate}>
            <PlusCircle size={18} className="mr-2" />
            Manage Subscriptions
          </Button>
        </div>
        <div className="grid grid-cols-1 @md:grid-cols-2 gap-4">
          {initialSubscriptions.map((sub) => (
            <Card key={sub.id} className="p-4">
              <div className="flex justify-between items-start">
                <div className="flex items-center">
                  <img
                    src={sub.logoUrl}
                    alt={sub.platform}
                    className="w-10 h-10 rounded-md mr-4"
                  />
                  <div>
                    <p className="font-bold text-primary">{sub.name}</p>
                    <p className="text-sm text-slate-600">
                      {sub.priceETB} ETB/month
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onEdit(sub)}
                  className="h-8 w-8"
                >
                  <Edit size={16} />
                </Button>
              </div>
              <ul className="mt-3 text-sm text-slate-500 list-disc list-inside space-y-1">
                {sub.perks.map((perk, i) => (
                  <li key={i}>{perk}</li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      </div>

      {/* --- Subscriber List & Recent Activity --- */}
      <AdminDataTable
        title="Subscriber List"
        columns={subscriberColumns}
        data={mockCreator.subscribersList}
      />
      <RecentActivityList
        title="Recent Subscription Activity"
        transactions={subscriptionTransactions}
      />
    </div>
  );
};

export default SubscriptionsTab;
